import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import Api from '../../api/userApi';
import useGeolocation from '../../hooks/useGeolocation';
import { hasValidLocation } from '../../utils/geoClient';
import LocationPermissionDialog from './LocationPermissionDialog';

/**
 * One-time location backfill for ALREADY-REGISTERED users.
 *
 * Users (including workers/contractors) who completed their profile before GPS
 * capture existed have no usable location — or carry the legacy New Delhi
 * placeholder. This component gently asks such users for their location once,
 * and saves it to their profile. It never blocks anything and can be dismissed.
 *
 * Mounted globally (next to InstallPrompt) so it works regardless of the page.
 */
const DISMISS_KEY = (userId) => `locationBackfillDismissed_${userId}`;

const LocationBackfillPrompt = () => {
  const { user, refreshUser } = useAuth();
  const { requesting, requestLocation } = useGeolocation();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Only prompt signed-in users who have at least started their profile and
    // do not already have a usable GPS location.
    if (!user?._id) return;
    if (!user.isBasicProfileComplete) return; // brand-new users use the registration flow
    // Server-synced location is the source of truth. If the user already has a
    // usable GPS location saved, never show the prompt — and close it if it was
    // already open (e.g. location was synced from another tab/page).
    if (hasValidLocation(user.address)) {
      setOpen(false);
      return;
    }

    try {
      if (localStorage.getItem(DISMISS_KEY(user._id))) return;
    } catch {
      // localStorage unavailable — fall through and prompt this session only
    }

    // Small delay so it doesn't fight with first paint / other prompts.
    const t = setTimeout(() => setOpen(true), 1500);
    return () => clearTimeout(t);
  }, [user]);

  const dismiss = () => {
    setOpen(false);
    try {
      if (user?._id) localStorage.setItem(DISMISS_KEY(user._id), '1');
    } catch {
      // ignore
    }
  };

  const handleEnable = async () => {
    const result = await requestLocation();
    if (result.granted && Array.isArray(result.coordinates)) {
      try {
        await axios.put(Api.userProfile, {
          address: { coordinates: result.coordinates }
        });
        await refreshUser?.();
        toast.success('Location saved. Thanks!');
      } catch (e) {
        toast.error('Could not save your location. Please try again later.');
      }
    }
    // Whether granted or denied, don't keep nagging.
    dismiss();
  };

  if (!open) return null;

  return (
    <LocationPermissionDialog
      open={open}
      loading={requesting}
      onEnable={handleEnable}
      onNotNow={dismiss}
    />
  );
};

export default LocationBackfillPrompt;
